'use client';

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import { DesktopNav } from './desktop-nav';
import { HeaderAccount } from './header-account';
import { HeaderLinkProvider } from './header-link';
import { HeaderLogo } from './logo';
import { MobileNav } from './mobile-nav';
import type {
  HeaderAccountState,
  HeaderAppearance,
  HeaderDrawerCallToAction,
  HeaderLinkAction,
  HeaderLinkComponent,
  HeaderNavItem,
} from './model';
import { useCloseAcrossBreakpoint } from './use-close-across-breakpoint';

const APPEARANCE_CLASS: Record<HeaderAppearance, string> = {
  solid: 'bg-od-bg-primary text-od-content-primary',
  'solid-secondary': 'bg-od-bg-secondary text-od-content-primary',
  transparent: 'bg-transparent text-od-content-primary',
  inverse: 'bg-transparent text-od-content-inverse-primary',
};

export type SiteHeaderProps = {
  navigation: HeaderNavItem[];
  /** App-owned controls between the logo and the navigation, such as an address field. */
  fields?: ReactNode;
  account: HeaderAccountState;
  /** The logo destination in the bar and the drawer. */
  home: HeaderLinkAction;
  linkComponent: HeaderLinkComponent;
  appearance?: HeaderAppearance;
  /** Replaces the content container's default width and horizontal padding utilities. */
  contentClassName?: string;
  drawerCallToAction?: HeaderDrawerCallToAction;
  /** Controls the mobile drawer. Omit to let the header own its state. */
  mobileMenu?: { open: boolean; onOpenChange: (open: boolean) => void };
  /** Runs when the customer opens or closes the drawer, not when the viewport closes it. */
  onMobileMenuToggle?: (open: boolean) => void;
};

export function SiteHeader({
  navigation,
  fields,
  account,
  home,
  linkComponent,
  appearance = 'solid',
  contentClassName,
  drawerCallToAction,
  mobileMenu,
  onMobileMenuToggle,
}: SiteHeaderProps) {
  const [ownMenuOpen, setOwnMenuOpen] = useState(false);
  const menuOpen = mobileMenu?.open ?? ownMenuOpen;
  const setMenuOpen = useCallback(
    (next: boolean) => {
      if (mobileMenu) mobileMenu.onOpenChange(next);
      else setOwnMenuOpen(next);
    },
    [mobileMenu],
  );
  const toggleMenu = useCallback(
    (next: boolean) => {
      if (next === menuOpen) return;
      setMenuOpen(next);
      onMobileMenuToggle?.(next);
    },
    [menuOpen, onMobileMenuToggle, setMenuOpen],
  );
  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
  useCloseAcrossBreakpoint({ open: menuOpen, close: closeMenu, openerVisibleOnDesktop: false });

  return (
    <HeaderLinkProvider value={linkComponent}>
      <header
        data-site-header
        data-appearance={appearance}
        className={`group/site-header flex justify-center ${APPEARANCE_CLASS[appearance]}`}
      >
        <div
          className={`grid h-(--site-header-height) w-full grid-cols-[auto_1fr_auto] items-center gap-x-3 ${contentClassName ?? 'max-w-[75rem] px-5'}`}
        >
          <HeaderLogo action={home} placement="bar" />
          <div className="flex min-w-0 items-center justify-end gap-3">
            {fields}
            <div className="hidden items-center gap-2 site-header-desktop:flex">
              <DesktopNav items={navigation} />
              <HeaderAccount state={account} />
            </div>
          </div>
          <div className="flex items-center justify-self-end gap-1">
            <MobileNav
              home={home}
              navigation={navigation}
              account={account}
              callToAction={drawerCallToAction ?? null}
              open={menuOpen}
              setOpen={toggleMenu}
            />
          </div>
        </div>
      </header>
    </HeaderLinkProvider>
  );
}
